import React from "react";
import { fmt } from "../../services/formatting";
import { getCurrencyLabel, getPlatformCurrency } from "../../services/currency";
import {
  sanitizeNonNegativeInput,
  sanitizePositiveIntegerInput,
} from "../../utils/nonNegativeInput";
import { sortTournamentsByName } from "../../services/tournamentSorting";
import {
  filterTournamentsByPlatformPreferences,
  getAvailableRegistroPlatforms,
  getSelectedRegistroPlatforms,
  toggleRegistroPlatformPreference,
} from "../../services/completePreferences";

/**
 * Registro form component for creating/editing registros
 */
export const RegistroForm = ({
  form,
  errors,
  tournaments,
  completePreferences = {},
  onCompletePreferencesChange = () => {},
  onSubmit,
  onCancel,
  isEditing,
  onChange,
  getTournament,
}) => {
  const [platformMenuOpen, setPlatformMenuOpen] = React.useState(false);
  const platformMenuRef = React.useRef(null);

  const handleChange = (field, value) => {
    onChange({ ...form, [field]: value });
  };

  const platformOptions = React.useMemo(
    () => getAvailableRegistroPlatforms(tournaments, form.plataforma),
    [form.plataforma, tournaments]
  );
  const selectedPlatformOptions = React.useMemo(
    () => getSelectedRegistroPlatforms(completePreferences, platformOptions),
    [completePreferences, platformOptions]
  );
  const selectedPlatformSet = React.useMemo(
    () => new Set(selectedPlatformOptions),
    [selectedPlatformOptions]
  );
  const platformSummary =
    selectedPlatformOptions.length === platformOptions.length
      ? "Todas"
      : `${selectedPlatformOptions.length} selecionada${
          selectedPlatformOptions.length === 1 ? "" : "s"
        }`;
  const filteredTournaments = React.useMemo(
    () =>
      filterTournamentsByPlatformPreferences(
        tournaments,
        completePreferences,
        form.plataforma
      ),
    [completePreferences, form.plataforma, tournaments]
  );
  const selectedTournament = getTournament(form.torneioId);
  const selectedTournamentCurrency =
    selectedTournament?.buyinCurrency ||
    getPlatformCurrency(selectedTournament?.plataforma || form.plataforma);

  const handlePlatformPreferenceToggle = (platform) => {
    const nextPreferences = toggleRegistroPlatformPreference(
      completePreferences,
      platform,
      platformOptions
    );
    const nextSelectedPlatforms = new Set(
      getSelectedRegistroPlatforms(nextPreferences, platformOptions)
    );
    const currentTournament = getTournament(form.torneioId);

    onCompletePreferencesChange(nextPreferences);

    if (
      currentTournament?.plataforma &&
      !nextSelectedPlatforms.has(currentTournament.plataforma)
    ) {
      onChange({ ...form, plataforma: "", torneioId: "" });
    }
  };

  const handleTournamentChange = (tournamentId) => {
    const tournament = getTournament(tournamentId);
    onChange({
      ...form,
      torneioId: tournamentId,
      plataforma: tournament?.plataforma || form.plataforma,
    });
  };

  React.useEffect(() => {
    if (!platformMenuOpen) return undefined;

    const handleDocumentMouseDown = (event) => {
      if (!platformMenuRef.current?.contains(event.target)) {
        setPlatformMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
  }, [platformMenuOpen]);

  let previewContent = null;
  let ticketToggle = null;

  if (selectedTournament?.modalidade === "SAT") {
    const insc = Number(form.inscricoes || 1);
    const buyin = Number(selectedTournament.buyin);
    const inv = buyin * insc;
    const tickets = Number(form.tickets || 0);
    const ticketValue = Number(selectedTournament.valorTicket || 0);
    const ganho = tickets * ticketValue;
    const lucro = ganho - inv;

    previewContent = (
      <div className="preview-box registro-form__preview">
        Investido: {fmt(inv, selectedTournamentCurrency)} ({insc}× {fmt(buyin, selectedTournamentCurrency)}) &nbsp;·&nbsp;
        Ganho em SAT:{" "}
        <span className="preview-box__positive">
          {tickets} × {fmt(ticketValue, selectedTournament?.ticketValueCurrency || selectedTournamentCurrency)} = {fmt(ganho, selectedTournament?.ticketValueCurrency || selectedTournamentCurrency)}
        </span>{" "}
        &nbsp;·&nbsp;
        Lucro SAT:{" "}
        <span className={lucro >= 0 ? "preview-box__positive" : "preview-box__danger"}>
          {lucro >= 0 ? "+" : ""}
          {fmt(lucro, selectedTournamentCurrency)}
        </span>
      </div>
    );
  } else if (selectedTournament) {
    const insc = Number(form.inscricoes || 1);
    const buyin = Number(selectedTournament.buyin);
    const inv = buyin * insc;
    const prem = Number(form.premiacao || 0);
    const lucro = prem - inv;
    const r = inv > 0 ? (((prem - inv) / inv) * 100).toFixed(1) : "—";

    ticketToggle = (
      <div className="ticket-toggle-box">
        <label className="ticket-toggle-label">
          <input
            type="checkbox"
            checked={form.isSatellite}
            onChange={(e) => handleChange("isSatellite", e.target.checked)}
          />
          Ticket
        </label>
      </div>
    );

    previewContent = (
      <div className="preview-box registro-form__preview">
        Investido: {fmt(form.isSatellite ? 0 : inv, selectedTournamentCurrency)} ({insc}× {fmt(buyin, selectedTournamentCurrency)}){" "}
        &nbsp;·&nbsp;
        Resultado:{" "}
        <span className={lucro >= 0 ? "preview-box__positive" : "preview-box__danger"}>
          {form.isSatellite ? "via ticket" : (lucro >= 0 ? "+" : "") + fmt(lucro, selectedTournamentCurrency)}
        </span>{" "}
        &nbsp;·&nbsp;
        ROI:{" "}
        <span className={Number(r) >= 0 ? "preview-box__positive" : "preview-box__danger"}>
          {form.isSatellite ? "—" : r + "%"}
        </span>
      </div>
    );
  }

  return (
    <div className="panel">
      <h2 className="form-title">
        ♠ {isEditing ? "Atualizar Registro" : "Nova Participação"}
      </h2>

      <div className="registro-form-grid">
        <div>
          <label className="field-label">Plataformas</label>
          <div className="platform-preference-control" ref={platformMenuRef}>
            <button
              className="input-field platform-preference-trigger"
              type="button"
              aria-expanded={platformMenuOpen}
              onClick={() => setPlatformMenuOpen((open) => !open)}
            >
              <span>{platformSummary}</span>
              <span className="platform-preference-trigger__arrow">▾</span>
            </button>

            {platformMenuOpen && (
              <div className="platform-preference-list">
                {platformOptions.map((platform) => (
                  <label key={platform} className="platform-preference-option">
                    <input
                      type="checkbox"
                      checked={selectedPlatformSet.has(platform)}
                      onChange={() => handlePlatformPreferenceToggle(platform)}
                    />
                    <span>{platform}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {errors.plataforma && (
            <div className="field-error">{errors.plataforma}</div>
          )}
        </div>

        <div>
          <label className="field-label">Torneio</label>
          <select
            className="input-field"
            value={form.torneioId}
            onChange={(e) => handleTournamentChange(e.target.value)}
          >
            <option value="">Selecione...</option>
            {sortTournamentsByName(filteredTournaments)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome} — {fmt(t.buyin, t.buyinCurrency)} ({t.modalidade})
                  {t.source === "common" && t.plataforma
                    ? ` · ${t.plataforma}`
                    : ""}
                </option>
              ))}
          </select>
          {errors.torneioId && (
            <div className="field-error">{errors.torneioId}</div>
          )}
        </div>

        <div>
          <label className="field-label">
            Inscrições <span className="optional-label">1 = sem rebuy</span>
          </label>
          <input
            className="input-field"
            type="number"
            min="1"
            placeholder="1"
            value={form.inscricoes}
            onChange={(e) =>
              handleChange("inscricoes", sanitizePositiveIntegerInput(e.target.value, 3))
            }
          />
          {errors.inscricoes && (
            <div className="field-error">{errors.inscricoes}</div>
          )}
        </div>

        {selectedTournament?.modalidade === "SAT" ? (
          <div>
            <label className="field-label">Tickets Recebidos</label>
            <input
              className="input-field"
              type="number"
              min="0"
              placeholder="0 se nenhum"
              value={form.tickets}
              onChange={(e) =>
                handleChange("tickets", sanitizeNonNegativeInput(e.target.value, 3))
              }
            />
            {errors.tickets && (
              <div className="field-error">{errors.tickets}</div>
            )}
          </div>
        ) : (
          <div>
            <label className="field-label">
              Premiação Recebida ({getCurrencyLabel(selectedTournamentCurrency)})
            </label>
            <input
              className="input-field"
              type="number"
              min="0"
              placeholder="0 se não houve prêmio"
              value={form.premiacao}
              onChange={(e) =>
                handleChange("premiacao", sanitizeNonNegativeInput(e.target.value, 9))
              }
            />
            {errors.premiacao && (
              <div className="field-error">{errors.premiacao}</div>
            )}
          </div>
        )}
      </div>

      {ticketToggle}

      <div className="registro-form__footer">
        {previewContent}
        <div className="registro-form__actions">
          <button className="btn-ghost" onClick={onCancel}>
            Cancelar
          </button>
          <button className="btn-red btn-confirm-action" onClick={onSubmit}>
            {isEditing ? "Salvar Alterações" : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegistroForm;
